import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { JsonEditor } from '../../_shared/components/JsonEditor';
import { apiFormSchema, HTTP_METHODS, API_STATUS, AUTH_POLICIES } from '../schema/apiSchema';
import { useCreateApi, useReplaceApi, useAuthPolicyOptions, useRateLimitOptions } from '../hooks/useApis';
import type { ApiFormValues } from '../schema/apiSchema';
import type { ApiRow } from '../types/api';

type ToastKind = 'success' | 'error';

const ROLES = ['ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_USER'];
const PERMISSIONS = ['read', 'write'] as const;

interface ApiFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: ApiRow | null;
  onClose: () => void;
  onSaved?: () => void;
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: 'var(--spacing-2) var(--spacing-4)',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-brand-primary)' : '2px solid transparent',
        background: 'none',
        color: active ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
        cursor: 'pointer',
        fontSize: 'var(--font-size-sm)',
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}

export function ApiFormDialog({ open, mode, initial, onClose, onSaved }: ApiFormDialogProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'basic' | 'auth' | 'rateLimit' | 'json'>('basic');
  const [toast, setToast] = useState<{ message: string; kind: ToastKind } | null>(null);

  const { data: authPolicies = [] } = useAuthPolicyOptions();
  const { data: rateLimitPolicies = [] } = useRateLimitOptions();

  // stale 은 폼에서 편집 불가 서버 상태 — 기본값을 disabled 로 변환하여 schema 준수
  const resolveFormStatus = (s?: string): 'active' | 'deprecated' | 'disabled' =>
    s === 'stale' ? 'disabled' : ((s as 'active' | 'deprecated' | 'disabled') ?? 'active');

  const form = useForm<ApiFormValues>({
    resolver: zodResolver(apiFormSchema),
    defaultValues: initial
      ? {
          name: initial.name,
          method: initial.method,
          path: initial.path,
          version: initial.version,
          status: resolveFormStatus(initial.status),
          description: initial.description,
          authPolicy: initial.authPolicy,
          requiredRoles: initial.requiredRoles,
          rbac: initial.rbac,
          rateLimitPolicyId: initial.rateLimitPolicyId,
          enabled: initial.enabled,
        }
      : {
          name: '',
          method: 'GET',
          path: '',
          version: 'v1',
          status: 'active',
          authPolicy: 'jwt',
          requiredRoles: [],
          enabled: true,
        },
  });

  const createApi = useCreateApi(
    (name) => {
      // #444 (UAT 2026-05-13): 등록 후 확인 방법 안내 메시지로 갱신.
      setToast({ message: t('gateway.apis.toast.createdVerify', { name }), kind: 'success' });
      setTimeout(() => { setToast(null); onSaved?.(); onClose(); }, 2500);
    },
    (err) => {
      const msg = (err as { message?: string })?.message ?? t('gateway.shared.error.message');
      setToast({ message: msg, kind: 'error' });
    }
  );

  const replaceApi = useReplaceApi(
    initial?.id ?? '',
    () => {
      setToast({ message: t('gateway.apis.toast.updated'), kind: 'success' });
      setTimeout(() => { setToast(null); onSaved?.(); onClose(); }, 1500);
    },
    (err) => {
      const msg = (err as { message?: string })?.message ?? t('gateway.shared.error.message');
      setToast({ message: msg, kind: 'error' });
    }
  );

  useEffect(() => {
    if (open) {
      setActiveTab('basic');
      if (initial) {
        form.reset({
          name: initial.name,
          method: initial.method,
          path: initial.path,
          version: initial.version,
          status: resolveFormStatus(initial.status),
          description: initial.description,
          authPolicy: initial.authPolicy,
          requiredRoles: initial.requiredRoles,
          rbac: initial.rbac,
          rateLimitPolicyId: initial.rateLimitPolicyId,
          enabled: initial.enabled,
        });
      } else {
        form.reset({
          name: '',
          method: 'GET',
          path: '',
          version: 'v1',
          status: 'active',
          authPolicy: 'jwt',
          requiredRoles: [],
          enabled: true,
        });
      }
    }
  }, [open, initial, form]);

  const watchedValues = form.watch();
  const authPolicy = form.watch('authPolicy');
  const isPending = createApi.isPending || replaceApi.isPending;

  const onSubmit = form.handleSubmit(
    (values) => {
      if (mode === 'create') {
        createApi.mutate(values);
      } else if (initial) {
        replaceApi.mutate(values);
      }
    },
    // #415: 폼 유효성 검사 실패 시 — 첫 번째 오류 필드가 있는 탭으로 전환하고 에러 토스트 표시
    (errors) => {
      const basicFields = ['name', 'method', 'path', 'version', 'status', 'description', 'enabled'];
      const authFields = ['authPolicy', 'requiredRoles', 'rbac'];
      const rateLimitFields = ['rateLimitPolicyId'];
      if (basicFields.some((f) => f in errors)) {
        setActiveTab('basic');
      } else if (authFields.some((f) => f in errors)) {
        setActiveTab('auth');
      } else if (rateLimitFields.some((f) => f in errors)) {
        setActiveTab('rateLimit');
      }
      setToast({ message: t('gateway.apis.form.validationError'), kind: 'error' });
    }
  );

  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    width: '100%',
    padding: 'var(--spacing-2) var(--spacing-3)',
    border: `1px solid ${hasError ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    boxSizing: 'border-box',
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    marginBottom: 'var(--spacing-1)',
  };

  const fieldStyle: React.CSSProperties = {
    marginBottom: 'var(--spacing-4)',
  };

  return (
    <FormDialog
      open={open}
      title={mode === 'create' ? t('gateway.apis.form.create') : t('gateway.apis.form.edit')}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={isPending ? t('gateway.shared.actions.saving') : t('gateway.shared.actions.save')}
      cancelLabel={t('gateway.shared.actions.cancel')}
    >
      {toast && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            marginBottom: 'var(--spacing-3)',
            padding: 'var(--spacing-2) var(--spacing-3)',
            background: toast.kind === 'error' ? 'var(--color-danger)' : 'var(--color-success)',
            color: 'var(--color-text-inverse)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {toast.message}
        </div>
      )}

      {/* Tabs */}
      <div
        role="tablist"
        aria-label={t('gateway.apis.form.tabsAriaLabel')}
        style={{ display: 'flex', borderBottom: '1px solid var(--color-surface-border)', marginBottom: 'var(--spacing-4)' }}
      >
        <TabButton active={activeTab === 'basic'} onClick={() => setActiveTab('basic')}>{t('gateway.apis.form.tabs.basic')}</TabButton>
        <TabButton active={activeTab === 'auth'} onClick={() => setActiveTab('auth')}>{t('gateway.apis.form.tabs.auth')}</TabButton>
        <TabButton active={activeTab === 'rateLimit'} onClick={() => setActiveTab('rateLimit')}>{t('gateway.apis.form.tabs.rateLimit')}</TabButton>
        <TabButton active={activeTab === 'json'} onClick={() => setActiveTab('json')}>{t('gateway.apis.form.tabs.json')}</TabButton>
      </div>

      {/* Basic Tab */}
      {activeTab === 'basic' && (
        <div role="tabpanel">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-4)' }}>
            <div style={{ gridColumn: '1 / -1', ...fieldStyle }}>
              <label htmlFor="api-name" style={labelStyle}>{t('gateway.apis.form.name.label')}</label>
              <input
                id="api-name"
                {...form.register('name')}
                placeholder="user-list"
                aria-invalid={!!form.formState.errors.name}
                style={inputStyle(!!form.formState.errors.name)}
              />
              {form.formState.errors.name && (
                <span role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>
                  {form.formState.errors.name.message}
                </span>
              )}
            </div>

            <div style={fieldStyle}>
              <label htmlFor="api-method" style={labelStyle}>{t('gateway.apis.form.method.label')}</label>
              <select id="api-method" {...form.register('method')} style={inputStyle(false)}>
                {HTTP_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <div style={fieldStyle}>
              <label htmlFor="api-path" style={labelStyle}>{t('gateway.apis.form.path.label')}</label>
              <input
                id="api-path"
                {...form.register('path')}
                placeholder={t('gateway.apis.form.path.placeholder')}
                aria-invalid={!!form.formState.errors.path}
                style={inputStyle(!!form.formState.errors.path)}
              />
              {form.formState.errors.path && (
                <span role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>
                  {form.formState.errors.path.message}
                </span>
              )}
            </div>

            <div style={fieldStyle}>
              <label htmlFor="api-version" style={labelStyle}>{t('gateway.apis.form.version.label')}</label>
              <input
                id="api-version"
                {...form.register('version')}
                placeholder={t('gateway.apis.form.version.placeholder')}
                aria-invalid={!!form.formState.errors.version}
                style={inputStyle(!!form.formState.errors.version)}
              />
              {form.formState.errors.version && (
                <span role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>
                  {form.formState.errors.version.message}
                </span>
              )}
            </div>

            <div style={fieldStyle}>
              <label htmlFor="api-status" style={labelStyle}>{t('gateway.apis.form.status.label')}</label>
              <select id="api-status" {...form.register('status')} style={inputStyle(false)}>
                {API_STATUS.map((s) => (
                  <option key={s} value={s}>
                    {t(`gateway.apis.form.statusOptions.${s}`)}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ gridColumn: '1 / -1', ...fieldStyle }}>
              <label htmlFor="api-description" style={labelStyle}>{t('gateway.apis.form.description.label')}</label>
              <textarea
                id="api-description"
                {...form.register('description')}
                rows={3}
                style={{ ...inputStyle(false), resize: 'vertical' }}
              />
            </div>

            <div style={fieldStyle}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)' }}>
                <input type="checkbox" {...form.register('enabled')} />
                {t('gateway.apis.form.enabled.label')}
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Auth/Permissions Tab */}
      {activeTab === 'auth' && (
        <div role="tabpanel">
          <div style={fieldStyle}>
            <label htmlFor="api-auth-policy" style={labelStyle}>{t('gateway.apis.form.authPolicy.label')}</label>
            <select id="api-auth-policy" {...form.register('authPolicy')} style={inputStyle(false)}>
              {authPolicies.length > 0
                ? authPolicies.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)
                : AUTH_POLICIES.map((p) => (
                    <option key={p} value={p}>
                      {t(`gateway.apis.form.authOptions.${p}`)}
                    </option>
                  ))}
            </select>
          </div>

          {authPolicy === 'jwt' && (
            <div style={fieldStyle}>
              <label style={labelStyle}>{t('gateway.apis.form.rbac.label')}</label>
              <table
                role="grid"
                aria-label={t('gateway.apis.form.rbac.matrixAriaLabel')}
                style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--font-size-sm)' }}
              >
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 'var(--spacing-2)', borderBottom: '1px solid var(--color-surface-border)' }}>
                      {t('gateway.apis.form.rbac.permissionHeader')}
                    </th>
                    {ROLES.map((role) => (
                      <th
                        key={role}
                        style={{ textAlign: 'center', padding: 'var(--spacing-2)', borderBottom: '1px solid var(--color-surface-border)' }}
                      >
                        {role.replace('ROLE_', '')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PERMISSIONS.map((perm) => (
                    <tr key={perm}>
                      <td style={{ padding: 'var(--spacing-2)', fontWeight: 500 }}>
                        {perm === 'read' ? t('gateway.apis.form.rbac.readPerm') : t('gateway.apis.form.rbac.writePerm')}
                      </td>
                      {ROLES.map((role) => {
                        const rbac = form.watch('rbac') ?? {};
                        const rolePerms = rbac[role] ?? [];
                        const checked = rolePerms.includes(perm);
                        return (
                          <td key={role} style={{ textAlign: 'center', padding: 'var(--spacing-2)' }}>
                            <input
                              type="checkbox"
                              aria-label={t('gateway.apis.form.rbac.checkboxAria', { role, perm })}
                              checked={checked}
                              onChange={(e) => {
                                const currentRbac = { ...(form.getValues('rbac') ?? {}) };
                                const current = [...(currentRbac[role] ?? [])];
                                if (e.target.checked) {
                                  currentRbac[role] = [...current, perm] as ('read' | 'write')[];
                                } else {
                                  currentRbac[role] = current.filter((p) => p !== perm) as ('read' | 'write')[];
                                }
                                form.setValue('rbac', currentRbac);
                              }}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Rate-Limit Tab */}
      {activeTab === 'rateLimit' && (
        <div role="tabpanel">
          <div style={fieldStyle}>
            <label htmlFor="api-rate-limit" style={labelStyle}>{t('gateway.apis.form.rateLimitPolicy.label')}</label>
            <select
              id="api-rate-limit"
              {...form.register('rateLimitPolicyId')}
              style={inputStyle(false)}
            >
              <option value="">{t('gateway.apis.form.rateLimit.none')}</option>
              {rateLimitPolicies.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* JSON Tab */}
      {activeTab === 'json' && (
        <div role="tabpanel">
          <JsonEditor
            value={watchedValues}
            readOnly
            height={360}
            ariaLabel={t('gateway.apis.form.jsonAriaLabel')}
          />
        </div>
      )}
    </FormDialog>
  );
}
